import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/store';
import Layer from '@/components/layers/Layer';
import {
  INLINE_LAYER_ADDED,
  LAYER_USER_INTERACTION,
  MODAL_SAVE_CLICKED,
  SAVED_QUERY_SELECTED,
  SAVED_QUERY_UPDATED,
  SEARCH_LOADED,
  START_NEW_QUERY,
  SUGGESTION_APPLIED,
  TABLE_SELECTED,
  TEMPLATE_LAYER_ADDED,
} from '@/actions/constants';
import { setPage } from '@/features/query-builder/queryBuilderSlice';
import { useAppDispatch } from '@/store/hooks';
import { useQueryId, useSeriesId } from '@/hooks';
import React from 'react';
import { LAST_UPDATE_METHOD } from './constants';
import { TLastUpdateMethod } from './types';

type State = {
  steps: Layer[];
  lastUpdateMethod: TLastUpdateMethod;
};

const initialState: State = {
  steps: [],
  lastUpdateMethod: LAST_UPDATE_METHOD.backendSync,
};

const UIStepsInteraction = (state: Draft<State>) => {
  state.lastUpdateMethod = LAST_UPDATE_METHOD.stepsSidebarInteraction;
};

export const queryBuilderStepsSlice = createSlice({
  name: 'queryBuilderStepsSlice',
  initialState,
  reducers: {
    setSteps: (
      state,
      action: PayloadAction<Pick<State, 'steps' | 'lastUpdateMethod'>>,
    ) => {
      state.steps = action.payload.steps;
      state.lastUpdateMethod = action.payload.lastUpdateMethod;
    },
    setLastUpdateMethod: (
      state,
      action: PayloadAction<State['lastUpdateMethod']>,
    ) => {
      state.lastUpdateMethod = action.payload;
    },
  },
  extraReducers: {
    [setPage.type]: (state) => {
      state.lastUpdateMethod = LAST_UPDATE_METHOD.rowsResultPagination;
    },
    [TABLE_SELECTED]: UIStepsInteraction,
    [SAVED_QUERY_SELECTED]: UIStepsInteraction,
    [SAVED_QUERY_UPDATED]: UIStepsInteraction,
    [SEARCH_LOADED]: UIStepsInteraction,
    [SUGGESTION_APPLIED]: UIStepsInteraction,
    [TEMPLATE_LAYER_ADDED]: UIStepsInteraction,
    [MODAL_SAVE_CLICKED]: UIStepsInteraction,
    [INLINE_LAYER_ADDED]: UIStepsInteraction,
    [LAYER_USER_INTERACTION]: (state, action) => {
      if (
        ['toggled', 'deleted', 'remove', 'updated', 'selected'].includes(
          action.payload.event,
        )
      ) {
        state.lastUpdateMethod = LAST_UPDATE_METHOD.stepsSidebarInteraction;
      }
    },
    [START_NEW_QUERY]: (state) => {
      state.lastUpdateMethod = LAST_UPDATE_METHOD.backendSync;
    },
  },
});
export const selectLastUpdateMethod = (state: RootState) =>
  state.queryBuilderStepsSlice.lastUpdateMethod;

export const { setSteps, setLastUpdateMethod } = queryBuilderStepsSlice.actions;

export const useResetLastUpdateMethod = () => {
  const dispatch = useAppDispatch();
  const queryId = useQueryId();
  const seriesId = useSeriesId();

  React.useEffect(() => {
    if (!queryId && !seriesId) {
      dispatch(setLastUpdateMethod(LAST_UPDATE_METHOD.backendSync));
    }
  }, [queryId, seriesId]);
};
